/**
 * Liquify displacement field — Tier 1 Group E final piece.
 *
 * The Liquify workspace deforms a layer via a brush-mutated
 * displacement field. The field stores, for every output cell,
 * the source-pixel offset where that output should sample from
 * (inverse-mapping, not forward-mapping):
 *
 *   output[x, y] = source[x + field(x, y).dx, y + field(x, y).dy]
 *
 * Storing the inverse instead of the forward map is what makes
 * the rasterizer single-pass — every output pixel reads exactly
 * one (bilinearly-interpolated) source sample. A forward map
 * would need scatter writes + hole filling.
 *
 * Architecture:
 *   - The field is a `Float32Array` of (dx, dy) pairs at every
 *     cell of a downscaled grid (caller chooses dims; the
 *     workspace uses ≤ 512 cells on the longest axis to bound
 *     memory + per-dab cost regardless of source dimensions).
 *   - Brush dabs mutate the field via tool-specific operators.
 *     Field cells are addressed in field-cell coords; the
 *     displacement values stored ARE in source-pixel units (so
 *     the rasterizer can add them directly to source positions
 *     without any scale dance).
 *   - The rasterizer reads the field with bilinear interpolation,
 *     adds the result to the source-space output position, and
 *     samples the source with bilinear interpolation.
 *
 * Pure module — no DOM, no Fabric, no React. Testable in isolation.
 * The workspace component (`LiquifyWorkspace.tsx`) is responsible
 * for the source⇄field unit conversion at the call sites.
 */

export type LiquifyTool =
  | 'push'
  | 'pull'
  | 'twirl-cw'
  | 'twirl-ccw'
  | 'pucker'
  | 'bloat'
  | 'reconstruct';

/** Radians of incremental rotation per unit of strength per dab.
 *  Calibrated so a default-pressure dab (strength ≈ brushSize/24
 *  in source pixels) on the workspace's mid-radius produces ~8.5°
 *  of perceived rotation — strong enough to be visible per click,
 *  small enough that a held drag doesn't immediately tear the
 *  mesh inside-out. Tune here; reflects everywhere. */
const TWIRL_RADIANS_PER_STRENGTH_UNIT = 0.003;

/** Lerp factor per unit of strength per dab for the Reconstruct
 *  tool. At strength=20 with full falloff, the lerp factor
 *  saturates at 1 → a single dab erases the cell's displacement
 *  fully. At lower strengths it tapers smoothly. */
const RECONSTRUCT_LERP_PER_STRENGTH_UNIT = 0.05;

export interface LiquifyBrushOptions {
  /** Active tool. */
  tool: LiquifyTool;
  /** Brush center, in field-cell coords. */
  cx: number;
  cy: number;
  /** Brush radius, in field cells. */
  radius: number;
  /** Peak per-dab displacement magnitude, in source pixels. The
   *  caller scales the user-facing Pressure slider into this. */
  strength: number;
  /** Brush hardness, 0..1. 0 = smooth gradient from center to
   *  edge; 1 = hard-edged dab with no falloff. Maps from the
   *  user-facing Density slider via `density / 100`. */
  hardness: number;
  /** Stroke direction for this dab, in source pixels. Used only
   *  by `push` and `pull` — other tools ignore it. Defaults to
   *  zero (single-click dab with no stroke direction). */
  strokeDx?: number;
  strokeDy?: number;
}

/**
 * Apply one brush dab. Mutates `field` in place. No-op when
 * `radius <= 0` or the dab falls entirely outside the field.
 *
 * Sign convention reminder: the field stores INVERSE displacement
 * (where to sample from), so the operators internally negate
 * stroke direction etc. so a "push right" stroke produces visually
 * right-moving content.
 */
export function applyLiquifyBrush(
  field: Float32Array,
  fw: number,
  fh: number,
  opts: LiquifyBrushOptions,
): void {
  const { tool, cx, cy, radius, strength, hardness } = opts;
  if (radius <= 0 || (strength === 0 && tool !== 'reconstruct')) return;
  if (fw <= 0 || fh <= 0) return;

  const x0 = Math.max(0, Math.floor(cx - radius));
  const y0 = Math.max(0, Math.floor(cy - radius));
  const x1 = Math.min(fw - 1, Math.ceil(cx + radius));
  const y1 = Math.min(fh - 1, Math.ceil(cy + radius));
  if (x0 > x1 || y0 > y1) return;

  const r2 = radius * radius;
  const h = Math.max(0, Math.min(1, hardness));
  // Push/pull use the normalized stroke direction; twirl scales
  // by a per-dab angle derived from `strength`.
  const sdx = opts.strokeDx ?? 0;
  const sdy = opts.strokeDy ?? 0;
  const strokeLen = Math.hypot(sdx, sdy);

  for (let fy = y0; fy <= y1; fy++) {
    const cellDy = fy - cy;
    for (let fx = x0; fx <= x1; fx++) {
      const cellDx = fx - cx;
      const d2 = cellDx * cellDx + cellDy * cellDy;
      if (d2 > r2) continue;
      const dist = Math.sqrt(d2);
      // Hardness-modulated falloff: a flat plateau of width
      // `hardness * radius` from the center, then a smoothstep
      // taper out to the edge. hardness=1 → full strength to the
      // edge (hard dab); hardness=0 → no plateau (soft dab).
      const t = dist / radius;
      let falloff: number;
      if (t <= h) {
        falloff = 1;
      } else {
        const u = (t - h) / (1 - h);
        falloff = 1 - u * u * (3 - 2 * u);
      }
      if (falloff <= 0) continue;

      const i = (fy * fw + fx) * 2;
      const fdx = field[i];
      const fdy = field[i + 1];

      switch (tool) {
        case 'push': {
          // Inverse field samples from BEHIND the stroke
          // direction so visually the content moves with the
          // stroke. strength caps per-dab magnitude.
          if (strokeLen === 0) break;
          const nx = sdx / strokeLen;
          const ny = sdy / strokeLen;
          const mag = strength * falloff;
          field[i] = fdx - nx * mag;
          field[i + 1] = fdy - ny * mag;
          break;
        }
        case 'pull': {
          // Pull TOWARD the cursor visually → inverse field
          // grows AWAY from cursor. Each dab pulls field cells
          // toward the cursor by an amount proportional to
          // `strength * falloff`.
          if (dist === 0) break;
          const nx = -cellDx / dist;
          const ny = -cellDy / dist;
          const mag = strength * falloff;
          field[i] = fdx - nx * mag;
          field[i + 1] = fdy - ny * mag;
          break;
        }
        case 'twirl-cw':
        case 'twirl-ccw': {
          // Incremental small-angle rotation of the cursor-
          // relative cell offset; the rotation acts on the
          // offset, not on the existing field value, so repeated
          // dabs accumulate angle linearly instead of compounding
          // into infinite spin. Visual CW = inverse-map CCW.
          //
          // Trade-off: the rotation delta is computed in field-
          // cell units, not source units, so twirl strength feels
          // slightly different across source resolutions.
          // Acceptable for v1; future refinement: pass
          // `sourcePerCell` so the delta scales into source-pixel
          // units before being stored.
          const sign = tool === 'twirl-cw' ? -1 : 1;
          const theta = sign * TWIRL_RADIANS_PER_STRENGTH_UNIT * strength * falloff;
          const cos = Math.cos(theta);
          const sin = Math.sin(theta);
          const rx = cellDx * cos - cellDy * sin;
          const ry = cellDx * sin + cellDy * cos;
          field[i] = fdx + (rx - cellDx);
          field[i + 1] = fdy + (ry - cellDy);
          break;
        }
        case 'pucker': {
          // Squeeze inward — inverse field grows OUTWARD so that
          // output positions near the cursor sample from positions
          // farther away.
          if (dist === 0) break;
          const nx = cellDx / dist;
          const ny = cellDy / dist;
          const mag = strength * falloff;
          field[i] = fdx + nx * mag;
          field[i + 1] = fdy + ny * mag;
          break;
        }
        case 'bloat': {
          // Expand outward — inverse field grows INWARD.
          if (dist === 0) break;
          const nx = -cellDx / dist;
          const ny = -cellDy / dist;
          const mag = strength * falloff;
          field[i] = fdx + nx * mag;
          field[i + 1] = fdy + ny * mag;
          break;
        }
        case 'reconstruct': {
          // Lerp the field toward zero. Stronger / harder brushes
          // erase faster; the lerp factor saturates at 1.
          const lerp = Math.min(1, RECONSTRUCT_LERP_PER_STRENGTH_UNIT * strength * falloff);
          field[i] = fdx * (1 - lerp);
          field[i + 1] = fdy * (1 - lerp);
          break;
        }
      }
    }
  }
}

/** Reset every cell of the field to zero. */
export function resetLiquifyField(field: Float32Array): void {
  field.fill(0);
}

export interface LiquifyRasterizeOptions {
  /** Sample source bilinearly (smoother but ~2× cost) vs nearest-
   *  neighbor (faster). Use nearest for live drag preview, bilinear
   *  for the Apply-time bake. */
  sourceFilter?: 'bilinear' | 'nearest';
}

/**
 * Inverse-map the source through the displacement field onto the
 * target canvas. Target dimensions can differ from source — the
 * routine handles the source⇄target⇄field mapping internally.
 *
 *   targetX → sourceX = targetX * source.width / target.width
 *   sourceX → fieldX  = sourceX * fw / source.width
 *
 * Each output pixel samples the field (bilinear), adds the
 * source-pixel displacement, and samples the source.
 */
export function rasterizeLiquify(
  source: HTMLCanvasElement,
  field: Float32Array,
  fw: number,
  fh: number,
  target: HTMLCanvasElement,
  opts?: LiquifyRasterizeOptions,
): void {
  const sw = source.width;
  const sh = source.height;
  const tw = target.width;
  const th = target.height;
  if (sw <= 0 || sh <= 0 || tw <= 0 || th <= 0) return;
  if (fw <= 0 || fh <= 0) return;

  const tctx = target.getContext('2d', { willReadFrequently: true });
  if (!tctx) return;
  // Snapshot the source pixels once. getImageData is ~10-30ms on
  // a 2K source — paid once per rasterize, not per pixel.
  const sctx = source.getContext('2d', { willReadFrequently: true });
  if (!sctx) return;
  const sData = sctx.getImageData(0, 0, sw, sh).data;
  const out = tctx.createImageData(tw, th);
  const oData = out.data;

  const sxPerTx = sw / tw;
  const syPerTy = sh / th;
  const fxPerSx = fw / sw;
  const fyPerSy = fh / sh;
  const filter = opts?.sourceFilter ?? 'bilinear';

  for (let ty = 0; ty < th; ty++) {
    const sy = ty * syPerTy;
    const fy = sy * fyPerSy;
    // Field bilinear weights for this row (one set of y-weights
    // shared across every column in the row).
    const fy0 = Math.max(0, Math.min(fh - 1, Math.floor(fy)));
    const fy1 = Math.max(0, Math.min(fh - 1, fy0 + 1));
    const fdy = fy - Math.floor(fy);
    const fdyInv = 1 - fdy;

    for (let tx = 0; tx < tw; tx++) {
      const sx = tx * sxPerTx;
      const fx = sx * fxPerSx;
      const fx0 = Math.max(0, Math.min(fw - 1, Math.floor(fx)));
      const fx1 = Math.max(0, Math.min(fw - 1, fx0 + 1));
      const fdx = fx - Math.floor(fx);
      const fdxInv = 1 - fdx;

      const i00 = (fy0 * fw + fx0) * 2;
      const i10 = (fy0 * fw + fx1) * 2;
      const i01 = (fy1 * fw + fx0) * 2;
      const i11 = (fy1 * fw + fx1) * 2;

      const w00 = fdxInv * fdyInv;
      const w10 = fdx * fdyInv;
      const w01 = fdxInv * fdy;
      const w11 = fdx * fdy;

      const dispX = field[i00] * w00 + field[i10] * w10 + field[i01] * w01 + field[i11] * w11;
      const dispY =
        field[i00 + 1] * w00 + field[i10 + 1] * w10 + field[i01 + 1] * w01 + field[i11 + 1] * w11;

      const ssx = sx + dispX;
      const ssy = sy + dispY;

      const oIdx = (ty * tw + tx) * 4;
      if (filter === 'nearest') {
        const ix = Math.round(ssx);
        const iy = Math.round(ssy);
        if (ix < 0 || ix >= sw || iy < 0 || iy >= sh) {
          oData[oIdx] = 0;
          oData[oIdx + 1] = 0;
          oData[oIdx + 2] = 0;
          oData[oIdx + 3] = 0;
        } else {
          const sIdx = (iy * sw + ix) * 4;
          oData[oIdx] = sData[sIdx];
          oData[oIdx + 1] = sData[sIdx + 1];
          oData[oIdx + 2] = sData[sIdx + 2];
          oData[oIdx + 3] = sData[sIdx + 3];
        }
      } else {
        // Bilinear source sample.
        if (ssx < 0 || ssx >= sw - 1 || ssy < 0 || ssy >= sh - 1) {
          // Out-of-bounds — clamp to edge for the in-range
          // dimension and zero for fully-outside positions. Edge-
          // clamp matches Photoshop Liquify's "Mesh Reconstruct
          // Options → Edge Mode: Stretch."
          const cx = Math.max(0, Math.min(sw - 1, Math.round(ssx)));
          const cy = Math.max(0, Math.min(sh - 1, Math.round(ssy)));
          const sIdx = (cy * sw + cx) * 4;
          const inBounds = ssx >= 0 && ssx < sw && ssy >= 0 && ssy < sh;
          oData[oIdx] = inBounds ? sData[sIdx] : 0;
          oData[oIdx + 1] = inBounds ? sData[sIdx + 1] : 0;
          oData[oIdx + 2] = inBounds ? sData[sIdx + 2] : 0;
          oData[oIdx + 3] = inBounds ? sData[sIdx + 3] : 0;
          continue;
        }
        const sx0 = Math.floor(ssx);
        const sy0 = Math.floor(ssy);
        const sx1 = sx0 + 1;
        const sy1 = sy0 + 1;
        const sdx = ssx - sx0;
        const sdy = ssy - sy0;
        const sdxInv = 1 - sdx;
        const sdyInv = 1 - sdy;
        const s00 = sdxInv * sdyInv;
        const s10 = sdx * sdyInv;
        const s01 = sdxInv * sdy;
        const s11 = sdx * sdy;

        const sBase00 = (sy0 * sw + sx0) * 4;
        const sBase10 = (sy0 * sw + sx1) * 4;
        const sBase01 = (sy1 * sw + sx0) * 4;
        const sBase11 = (sy1 * sw + sx1) * 4;

        oData[oIdx] =
          sData[sBase00] * s00 + sData[sBase10] * s10 + sData[sBase01] * s01 + sData[sBase11] * s11;
        oData[oIdx + 1] =
          sData[sBase00 + 1] * s00 +
          sData[sBase10 + 1] * s10 +
          sData[sBase01 + 1] * s01 +
          sData[sBase11 + 1] * s11;
        oData[oIdx + 2] =
          sData[sBase00 + 2] * s00 +
          sData[sBase10 + 2] * s10 +
          sData[sBase01 + 2] * s01 +
          sData[sBase11 + 2] * s11;
        oData[oIdx + 3] =
          sData[sBase00 + 3] * s00 +
          sData[sBase10 + 3] * s10 +
          sData[sBase01 + 3] * s01 +
          sData[sBase11 + 3] * s11;
      }
    }
  }
  tctx.putImageData(out, 0, 0);
}
